import React, { useCallback, useEffect, useRef, useState } from 'react';

import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';
import { Hyperlink } from '@motorway/mw-highway-code/alpha';
import { BUTTON_AS_TYPE, BUTTON_TYPE, SIZE, VARIANT } from '@motorway/mw-highway-code/enums';

import { getCookieValue } from 'Utilities';
import { SP_COOKIE_POLICY_REMINDER_EVENTS } from 'Utilities/analytics/analyticsCookieBanner';
import { useIsEmbeddedView } from 'Utilities/hooks/useIsEmbeddedView';

import { CONSENT_COOKIE_POLICY_VERSION } from './consentService';
import { CONSENT_COOKIE_NAME, COOKIE_POLICY_PATH } from './cookieConsent.constants';
import { storeConsent } from './cookieConsent.helpers';
import LocalTexts from './CookiePolicyReminderBanner.texts.json';

import overlayStyles from './CookieConsent.module.scss';
import styles from './CookiePolicyReminderBanner.module.scss';

const LocalT = new MDText(LocalTexts);

const COUNTDOWN_SECONDS = 10;

export const shouldShowReminder = (): boolean => {
  const raw = getCookieValue(CONSENT_COOKIE_NAME);

  if (!raw) {
    return false;
  }

  try {
    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed !== 'object') {
      return false;
    }

    return parsed.cookie_policy_version !== CONSENT_COOKIE_POLICY_VERSION;
  } catch {
    return false;
  }
};

export const CookiePolicyReminderBanner = () => {
  const isEmbeddedView = useIsEmbeddedView();
  const [visible, setVisible] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const dismiss = useCallback(() => {
    setVisible(false);
    storeConsent({});

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isEmbeddedView || !shouldShowReminder()) {
      return;
    }

    setVisible(true);
  }, [isEmbeddedView]);

  useEffect(() => {
    if (!visible) {
      return undefined;
    }

    intervalRef.current = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [visible, dismiss]);

  useEffect(() => {
    if (countdown === 0 && visible) {
      dismiss();
    }
  }, [countdown, visible, dismiss]);

  const handleOkay = () => {
    SP_COOKIE_POLICY_REMINDER_EVENTS.OKAY_CLICKED();
    dismiss();
  };

  const handleLearnMore = () => {
    SP_COOKIE_POLICY_REMINDER_EVENTS.LEARN_MORE_CLICKED(COOKIE_POLICY_PATH);
    dismiss();
  };

  if (!visible || isEmbeddedView) {
    return null;
  }

  const okayLabel = <>{LocalT.translate('okay')} <span aria-hidden="true">({countdown}s)</span></>;

  const cookieLink = <Hyperlink
    underline
    as={BUTTON_AS_TYPE.ANCHOR}
    href={COOKIE_POLICY_PATH}
    label={LocalT.translate('cookiePolicyLink')}
    onClick={handleLearnMore}
    size={SIZE.SMALL}
    target='_blank'
  />;

  return (
    <div className={overlayStyles.overlay}>
      <section
        aria-label="Cookie policy update"
        className={styles.banner}
      >
        <p data-thc-style-override className={styles.text}>
          {LocalT.translate('cookieReminder', { link: cookieLink })}
        </p>
        <Button
          label={okayLabel}
          onClick={handleOkay}
          size={SIZE.SMALL}
          type={BUTTON_TYPE.BUTTON}
          variant={VARIANT.SECONDARY}
        />
        <span aria-atomic="true" aria-live="polite" className={styles.srOnly}>
          {countdown === 5 ? 'This banner will close in 5 seconds' : ''}
        </span>
      </section>
    </div>
  );
};
